import { useEffect, useMemo, useState } from "react";
import { useApolloClient } from "@apollo/client";
import { type MinimalQueryResult } from "@jsq/ds/types";
import {
  GetTaxDataSourceAssignmentsDocument,
  useGetPositionsForCaseQuery,
  useGetTaxReportingDataQuery,
} from "~/graphql/supergraphHooks";
import {
  type GetTaxDataSourceAssignmentsQuery,
  type GetTaxDataSourceAssignmentsQueryVariables,
} from "~/graphql/supergraphTypes";
import { POSITION_CASE_TYPES } from "../Documents/DocumentManagement/DocumentDetailsRightSidebar/DocumentStatusCard/FatcaAssignmentModal/constants";
import { TAX_DOCUMENT_BASE_TYPES } from "../Documents/TaxForms/utils/documentCategoryHelpers";
import { getInvestingProfileName } from "../Profiles/utils";
import {
  buildCrsOverviewRows,
  buildFatcaOverviewRow,
  type CasePosition,
  type ComplianceCaseData,
  type CrsOverviewRow,
  type FatcaOverviewRow,
} from "./utils";

export type UseStandaloneFatcaCrsDataParams = {
  caseId: number;
  complianceCase: ComplianceCaseData | null | undefined;
};

export type StandaloneFatcaCrsData = {
  fatcaRow: FatcaOverviewRow | null;
  crsRows: CrsOverviewRow[];
  loading: boolean;
  assignmentsError: Error | null;
  queryResults: MinimalQueryResult[];
};

type Assignment = NonNullable<
  GetTaxDataSourceAssignmentsQuery["taxDataSourceAssignments"]
>[number];

export const useStandaloneFatcaCrsData = ({
  caseId,
  complianceCase,
}: UseStandaloneFatcaCrsDataParams): StandaloneFatcaCrsData => {
  const skipQueries = !caseId || caseId <= 0;
  const apolloClient = useApolloClient();
  const rootProfileId = complianceCase?.investingProfile?.id;

  const taxReportingResult = useGetTaxReportingDataQuery({
    variables: { caseId },
    skip: skipQueries,
    fetchPolicy: "network-only",
  });

  const positionsResult = useGetPositionsForCaseQuery({
    variables: {
      profileId: rootProfileId ?? 0,
      caseTypes: [...POSITION_CASE_TYPES],
    },
    skip: skipQueries || !rootProfileId,
    fetchPolicy: "network-only",
  });

  const taxReportingData = useMemo(
    () => taxReportingResult.data?.taxReportingData ?? [],
    [taxReportingResult.data?.taxReportingData],
  );

  const positions: CasePosition[] = useMemo(
    () =>
      (positionsResult.data?.complianceProfile?.positions ?? [])
        .filter((p) => p.onboardingCase)
        .map((p) => ({
          caseId: p.onboardingCase!.id,
          entityName: p.onboardingCase!.externalEntity?.name ?? null,
          rootProfileName: getInvestingProfileName(p.onboardingCase!.investingProfile),
        })),
    [positionsResult.data?.complianceProfile?.positions],
  );

  const taxDocumentIds = useMemo(() => {
    if (!complianceCase || !rootProfileId) return [];
    const taxBaseTypes: readonly string[] = TAX_DOCUMENT_BASE_TYPES;
    return (complianceCase.documents ?? [])
      .filter(
        (d) =>
          d.profileId === rootProfileId &&
          taxBaseTypes.includes(d.documentPurpose?.baseType ?? ""),
      )
      .map((d) => d.id)
      .sort((a, b) => a - b);
  }, [complianceCase, rootProfileId]);

  const documentIdsKey = taxDocumentIds.join(",");
  const [loadedAssignments, setLoadedAssignments] = useState<{
    key: string | null;
    data: Assignment[];
    error: Error | null;
  }>({ key: null, data: [], error: null });

  useEffect(() => {
    let cancelled = false;
    const ids = documentIdsKey ? documentIdsKey.split(",").map(Number) : [];
    const commit = (data: Assignment[], error: Error | null) => {
      if (!cancelled) setLoadedAssignments({ key: documentIdsKey, data, error });
    };
    if (skipQueries || ids.length === 0) {
      commit([], null);
      return;
    } else {
      Promise.all(
        ids.map((documentId) =>
          apolloClient.query<
            GetTaxDataSourceAssignmentsQuery,
            GetTaxDataSourceAssignmentsQueryVariables
          >({
            query: GetTaxDataSourceAssignmentsDocument,
            variables: { input: { documentId } },
            fetchPolicy: "network-only",
          }),
        ),
      )
        .then((results) => {
          commit(
            results.flatMap((r) => r.data?.taxDataSourceAssignments ?? []),
            null,
          );
        })
        .catch((err: unknown) => {
          const error = err instanceof Error ? err : new Error(String(err));
          commit([], error);
        });
    }
    return () => {
      cancelled = true;
    };
  }, [apolloClient, documentIdsKey, skipQueries]);

  const assignments = loadedAssignments.data;
  const assignmentsLoading = loadedAssignments.key !== documentIdsKey;
  const assignmentsError = loadedAssignments.error;

  const crsRows = useMemo(() => {
    if (!complianceCase || assignmentsLoading || assignmentsError) return [];
    return buildCrsOverviewRows({
      complianceCase,
      positions,
      taxReportingData,
      assignments,
    });
  }, [
    complianceCase,
    positions,
    taxReportingData,
    assignments,
    assignmentsLoading,
    assignmentsError,
  ]);

  // FATCA is reported per root profile (not per position), but is only relevant when the
  // case has at least one position; suppress the FATCA section when there are no CRS rows.
  const fatcaRow = useMemo(() => {
    if (!complianceCase || assignmentsLoading || assignmentsError) return null;
    if (crsRows.length === 0) return null;
    return buildFatcaOverviewRow({ complianceCase, taxReportingData, assignments });
  }, [
    complianceCase,
    taxReportingData,
    assignments,
    assignmentsLoading,
    assignmentsError,
    crsRows.length,
  ]);

  const loading = taxReportingResult.loading || positionsResult.loading || assignmentsLoading;

  return {
    fatcaRow,
    crsRows,
    loading,
    assignmentsError: loadedAssignments.error,
    queryResults: [taxReportingResult, positionsResult],
  };
};
